import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from '@tanstack/react-router';
import type { UseFormReturn } from 'react-hook-form';

import type { DeliverablesFormSchema } from '@/_pages/agreements/shared/deliverables';
import type { AgreementFormSchema } from '@/_pages/agreements/shared/schemas';
import { useBoolean } from '@/_shared';

export function useEditableState(
  form: UseFormReturn<AgreementFormSchema>,
  value: DeliverablesFormSchema[],
  isEditable: boolean
) {
  const [shouldUpdate, setShouldUpdate] = useBoolean(false);
  const { pathname } = useLocation();
  const [localDeliverableData, setLocalDeliverableData] = useState(value);

  const handleLocalDeliverablesDataUpdate = useCallback(
    (newDeliverables: DeliverablesFormSchema[]) => {
      setLocalDeliverableData((oldDeliverables) =>
        (oldDeliverables ?? []).map((row, index) => {
          const n = newDeliverables.find((d) => d.id === row.id);
          if (n && oldDeliverables?.[index]) {
            return {
              ...oldDeliverables[index]!,
              ...n,
              matchingDateRange: null,
            };
          }
          return { ...row, matchingDateRange: null };
        })
      );

      setShouldUpdate.setTrue();
    },
    [setShouldUpdate]
  );

  // !important
  const resetFormState = useCallback(() => {
    const formValues = form.getValues();
    form.reset(formValues, { keepDirty: false });
  }, [form]);

  // on add or remove row from table
  useEffect(() => {
    if (localDeliverableData === null) return;
    if (localDeliverableData.length !== value.length) {
      if (value.length > localDeliverableData.length) {
        value.forEach((row) => {
          if (!localDeliverableData.find((d) => d.id === row.id)) {
            setLocalDeliverableData((old) => [...(old ?? []), row]);
          }
        });
      }

      if (value.length < localDeliverableData.length) {
        // removing code here
        localDeliverableData.forEach((row) => {
          if (!value.find((d) => d.id === row.id)) {
            setLocalDeliverableData((old) => (old ?? []).filter((d) => d.id !== row.id));
          }
        });
      }

      setShouldUpdate.setTrue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDeliverableData, localDeliverableData?.length, value.length]);

  useEffect(() => {
    if (pathname.indexOf('/agreements/deliverables/') < 0) {
      setLocalDeliverableData(value);
    } else if (!isEditable) {
      setLocalDeliverableData(value);
    }
  }, [isEditable, pathname, value]);

  return useMemo(
    () => ({
      localDeliverableData,
      shouldUpdate,
      resetFormState,
      handleLocalDeliverablesDataUpdate,
      setShouldUpdate,
    }),
    [localDeliverableData, shouldUpdate, resetFormState, handleLocalDeliverablesDataUpdate, setShouldUpdate]
  );
}
